import React, { useState, useEffect, useRef } from "react";
import { Popover, Checkbox, Tag, Skeleton, Tooltip } from "antd";
import { SEQUENCE_STYLES, BROADCAST_STATUS } from "../../constants/CampaignConstants.jsx";

const CustomSelect = ({
  broadcasts = [],
  sequences = [],
  onSelectExecutionList,
  defaultChecked,
  loading,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [selectedBroadcastIds, setSelectedBroadcastIds] = useState(() => {
    if (defaultChecked) {
      return broadcasts
        .filter((b) => defaultChecked.includes(b.id))
        .map((b) => b.id);
    }
    return broadcasts.map((b) => b.id);
  });

  const [selectedSequenceIds, setSelectedSequenceIds] = useState(() => {
    if (defaultChecked) {
      return sequences
        .filter((s) => defaultChecked.includes(s.id))
        .map((s) => s.id);
    }
    return sequences.map((s) => s.id);
  });

  const prevSelectionRef = useRef({ broadcasts: [], sequences: [] });

  // Calculate current selections
  const currentSelection = {
    broadcasts: selectedBroadcastIds,
    sequences: selectedSequenceIds,
  };

  // Only trigger callback when selections actually change
  useEffect(() => {
    const hasBroadcastsChanged =
      prevSelectionRef.current.broadcasts.length !==
        selectedBroadcastIds.length ||
      selectedBroadcastIds.some(
        (id) => !prevSelectionRef.current.broadcasts.includes(id)
      );

    const hasSequencesChanged =
      prevSelectionRef.current.sequences.length !==
        selectedSequenceIds.length ||
      selectedSequenceIds.some(
        (id) => !prevSelectionRef.current.sequences.includes(id)
      );

    if (hasBroadcastsChanged || hasSequencesChanged) {
      // Map selected ids to broadcast_id and sequence_id for the callback
      const selectedBroadcastKeys = broadcasts
        .filter((b) => selectedBroadcastIds.includes(b.id))
        .map((b) => b.broadcast_id)
        .filter(Boolean); // filter out undefined/null

      const selectedSequenceKeys = sequences
        .filter((s) => selectedSequenceIds.includes(s.id))
        .map((s) => s.sequence_id || s.campaign_sequence_id)
        .filter(Boolean); // filter out undefined/null

      const includeBroadcasts =
        selectedBroadcastIds.length === broadcasts.length &&
        broadcasts.length > 0
          ? "all"
          : selectedBroadcastKeys.join(", ");

      const includeSequences =
        selectedSequenceIds.length === sequences.length && sequences.length > 0
          ? "all"
          : selectedSequenceKeys.join(", ");

      onSelectExecutionList({
        include_broadcasts: includeBroadcasts,
        include_sequences: includeSequences,
      });

      prevSelectionRef.current = {
        broadcasts: [...selectedBroadcastIds],
        sequences: [...selectedSequenceIds],
      };
    }
  }, [
    selectedBroadcastIds,
    selectedSequenceIds,
    broadcasts,
    sequences,
    onSelectExecutionList,
  ]);

  // Reset selections when all are unchecked
  useEffect(() => {
    if (
      broadcasts.length + sequences.length > 0 &&
      selectedBroadcastIds.length === 0 &&
      selectedSequenceIds.length === 0
    ) {
      setSelectedBroadcastIds(broadcasts.map((b) => b.id));
      setSelectedSequenceIds(sequences.map((s) => s.id));
    }
  }, [selectedBroadcastIds, selectedSequenceIds, broadcasts, sequences]);

  const handleAllBroadcastsChange = (e) => {
    const ids = e.target.checked ? broadcasts.map((b) => b.id) : [];
    setSelectedBroadcastIds(ids);
  };

  const handleAllSequencesChange = (e) => {
    const ids = e.target.checked ? sequences.map((s) => s.id) : [];
    setSelectedSequenceIds(ids);
  };

  const handleTagClose = (idOrType, type = null) => {
    if (idOrType === "all-broadcasts") {
      setSelectedBroadcastIds([]);
    } else if (idOrType === "all-sequences") {
      setSelectedSequenceIds([]);
    } else if (type === "broadcast") {
      setSelectedBroadcastIds((prev) => prev.filter((bId) => bId !== idOrType));
    } else {
      setSelectedSequenceIds((prev) => prev.filter((sId) => sId !== idOrType));
    }
  };

  const isAllBroadcastsSelected =
    broadcasts.length > 0 && selectedBroadcastIds.length === broadcasts.length;

  const isAllSequencesSelected =
    sequences.length > 0 && selectedSequenceIds.length === sequences.length;

  const selectedItems = [];

  if (isAllBroadcastsSelected) {
    selectedItems.push({
      id: "all-broadcasts",
      name: "All Broadcasts",
      type: "broadcast",
    });
  } else {
    selectedBroadcastIds.forEach((id) => {
      const item = broadcasts.find((b) => b.id === id);
      if (item) selectedItems.push({ ...item, type: "broadcast" });
    });
  }

  if (isAllSequencesSelected) {
    selectedItems.push({
      id: "all-sequences",
      name: "All Sequences",
      type: "sequence",
    });
  } else {
    selectedSequenceIds.forEach((id) => {
      const item = sequences.find((s) => s.id === id);
      if (item) selectedItems.push({ ...item, type: "sequence" });
    });
  }

  if (broadcasts.length === 0) {
    selectedItems.push({
      id: "all-broadcasts",
      name: "All Broadcasts",
      type: "broadcast",
    });
  }

  if (sequences.length === 0) {
    selectedItems.push({
      id: "all-sequences",
      name: "All Sequences",
      type: "sequence",
    });
  }

  const maxVisibleTags = 2;
  const visibleTags = selectedItems.slice(0, maxVisibleTags);
  const remainingCount = selectedItems.length - maxVisibleTags;

  const dropdownContent = (
    <div className="w-full max-h-[65vh] overflow-y-auto px-2 py-4">
      <div className="w-full h-full overflow-y-hidden">
        {/* <h4 className="font-semibold mt-3 border-b border-gray-200 pb-3">
          Broadcast
        </h4> */}
        {loading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : (
          <>
            <Checkbox
              checked={isAllBroadcastsSelected}
              onChange={handleAllBroadcastsChange}
              className="font-semibold mb-[10px]"
              disabled={broadcasts.length === 0}
            >
              All Broadcasts
            </Checkbox>
            <div className="ml-2 flex flex-col transition-all duration-300   gap-2">
              {broadcasts.map((broadcast) => (
                <>
                  <div className="border-t border-gray-200"></div>
                  <div
                    key={broadcast.id}
                    className={`rounded-lg px-3 py-1 cursor-pointer ${
                      selectedBroadcastIds.includes(broadcast.id)
                        ? "bg-[#25C27729]"
                        : ""
                    }`}
                    onClick={() => {
                      const isSelected = selectedBroadcastIds.includes(
                        broadcast.id
                      );
                      const newIds = isSelected
                        ? selectedBroadcastIds.filter(
                            (id) => id !== broadcast.id
                          )
                        : [...selectedBroadcastIds, broadcast.id];
                      setSelectedBroadcastIds(newIds);
                    }}
                  >
                    {broadcast.name && (
                      <Tooltip title={broadcast.name}>
                        <div className="flex flex-col justify-between items-start gap-2 py-[2.5px]">
                          <div className="truncate font-semibold">
                            {broadcast.name.length > 35
                              ? `${broadcast.name.slice(0, 35)}...`
                              : broadcast.name}
                          </div>

                          {/* Broadcast Status */}
                          <div
                            className="flex shrink-1 items-center gap-1.5 px-[7px] py-[1px] rounded-md"
                            style={{
                              background:
                                BROADCAST_STATUS[broadcast.broadcast_status]
                                  ?.bg,
                              color:
                                BROADCAST_STATUS[broadcast.broadcast_status]
                                  ?.color,
                              border: `1px solid ${
                                BROADCAST_STATUS[broadcast.broadcast_status]
                                  ?.color
                              }`,
                            }}
                          >
                            {BROADCAST_STATUS[broadcast.broadcast_status]
                              ?.icon && (
                              <img
                                src={
                                  BROADCAST_STATUS[broadcast.broadcast_status]
                                    ?.icon
                                }
                                alt={
                                  BROADCAST_STATUS[broadcast.broadcast_status]
                                    ?.text
                                }
                              />
                            )}
                            <p className="capitalize">
                              {
                                BROADCAST_STATUS[broadcast.broadcast_status]
                                  ?.text
                              }
                            </p>
                          </div>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="border-t w-full border-gray-200 my-[12px]"></div>

      <div className="w-full h-full overflow-y-hidden">
        {/* <h4 className="font-semibold mt-3 border-b border-gray-200 pb-3">
          Sequences
        </h4> */}
        {loading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : (
          <>
            <Checkbox
              checked={isAllSequencesSelected}
              onChange={handleAllSequencesChange}
              className="font-semibold mb-[10px]"
              disabled={sequences.length === 0}
            >
              All Sequences
            </Checkbox>
            <div className="ml-2  flex flex-col gap-2">
              {sequences.map((sequence) => (
                <>
                  <div className="border-t border-gray-200"></div>
                  <div
                    key={sequence.id}
                    className={`rounded-lg px-3 py-1 cursor-pointer ${
                      selectedSequenceIds.includes(sequence.id)
                        ? "bg-[#25C27729]"
                        : ""
                    }`}
                    onClick={() => {
                      const isSelected = selectedSequenceIds.includes(
                        sequence.id
                      );
                      const newIds = isSelected
                        ? selectedSequenceIds.filter((id) => id !== sequence.id)
                        : [...selectedSequenceIds, sequence.id];
                      setSelectedSequenceIds(newIds);
                    }}
                  >
                    <Tooltip title={sequence.name}>
                      <div className="flex flex-col truncate overflow-hidden items-start gap-2 py-[2.5px]">
                        <div className="truncate font-semibold">
                          {sequence.name.length > 35
                            ? `${sequence.name.slice(0, 35)}...`
                            : sequence.name}
                        </div>

                        {/* Sequence Status */}
                        <div
                          className="flex shrink-1 items-center gap-1.5 px-[7px] py-[1px] rounded-md"
                          style={{
                            background:
                              SEQUENCE_STYLES[sequence.campaign_sequence_status]
                                ?.bg,
                            color:
                              SEQUENCE_STYLES[sequence.campaign_sequence_status]
                                ?.color,
                            border: `1px solid ${
                              SEQUENCE_STYLES[sequence.campaign_sequence_status]
                                ?.color
                            }`,
                          }}
                        >
                          {SEQUENCE_STYLES[sequence.campaign_sequence_status]
                            ?.icon && (
                            <img
                              src={
                                SEQUENCE_STYLES[
                                  sequence.campaign_sequence_status
                                ]?.icon
                              }
                              alt={
                                SEQUENCE_STYLES[
                                  sequence.campaign_sequence_status
                                ]?.text
                              }
                            />
                          )}
                          <p className="capitalize">
                            {
                              SEQUENCE_STYLES[sequence.campaign_sequence_status]
                                ?.text
                            }
                          </p>
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                </>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={isDropdownOpen}
      onOpenChange={setDropdownOpen}
      content={dropdownContent}
      trigger="click"
      placement="bottomLeft"
      overlayClassName="w-[23%]"
    >
      {/* Changed container structure */}
      <div className="mt-3 flex border border-gray-300 rounded-lg py-[2.5px] pr-2 rounded cursor-pointer items-center w-full">
        <div className="flex flex-wrap items-center gap-2 p-2 w-full min-w-0">
          {" "}
          {/* Added min-w-0 */}
          {visibleTags.map((item) => (
            <Tag
              key={item.id}
              closable
              onClose={() => handleTagClose(item.id, item.type)}
              className="flex items-center m-0 bg-[#d8fdd2] border border-white rounded-md flex-shrink-0 max-w-[200px]" // Added max-width
            >
              {item.name && (
                <Tooltip title={item.name}>
                  <div className="truncate">
                    {item.name.length > 20
                      ? `${item.name.slice(0, 20)}...`
                      : item.name}
                  </div>
                </Tooltip>
              )}
            </Tag>
          ))}
          {remainingCount > 0 && (
            <Tag className="m-0 border bg-[#25C27729] border border-white rounded-md flex-shrink-0">
              +{remainingCount} more
            </Tag>
          )}
        </div>
        <div>
          <svg
            className={`ml-auto shrink-0 text-gray-600 transition-transform duration-200 ${
              isDropdownOpen ? "rotate-180" : ""
            }`}
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="currentColor"
          >
            <path d="M0 0L5 6L10 0H0Z" />
          </svg>
        </div>
      </div>
    </Popover>
  );
};

export default React.memo(CustomSelect);
